// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Signer, Contract, BytesLike, ethers } from 'ethers'
import { Erc20Abi, IPoolBaseAbi, PoolAbi, PoolDynamicAbi, PoolFlexAbi } from './Abis'
import { ActivityEnum, ChainError, PoolTypeEnum } from './Enums'
import { logOT } from './loggingShared'
import { EthAddr, ProviderReadOnly, ProviderWritable, TxHash } from './SchemaTypes'
import { txUUID, wsServerActionPoolTXRequest, wsServerActionPoolTX } from './WsServerActions'
import { ERC20Wei, erc20WeiToNumber } from './conversions'
import { Pool } from '@typechain-types/contracts'

export const adminReleaseWithdrawsForFlex = async (
  webSocket: WebSocket | undefined,
  borrowerSigner: Signer,
  poolAddr: EthAddr,
  eventId: string,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('borrowerReleaseWithdrawsForFlex ')
  const uuid = txUUID()
  webSocket?.send(
    wsServerActionPoolTXRequest(ActivityEnum.POOL_RELEASE_FLEX_WITHDRAWAL, poolAddr, uuid, chainConfigurationName)
  )
  const providerWritable = borrowerSigner.provider as ProviderWritable
  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, providerWritable)
  const poolType = Number(await poolBaseContract.poolType())
  if (poolType === PoolTypeEnum.FlexibleRate) {
    const poolFlexContract = new Contract(poolAddr, PoolFlexAbi, borrowerSigner)

    const tx2 = await poolFlexContract.releaseWithdrawal(eventId)

    webSocket?.send(
      wsServerActionPoolTX(
        ActivityEnum.POOL_RELEASE_FLEX_WITHDRAWAL,
        poolAddr,
        uuid,
        tx2.hash as TxHash,
        chainConfigurationName
      )
    )

    tx2.wait(1)

    logOT.debug('borrowerReleaseWithdrawsForFlex success', poolAddr)
  } else {
    throw new Error('Pool is not flexible rate')
  }
}

export const borrowerTransferRepayFlexWithdrawal = async (
  webSocket: WebSocket | undefined,
  borrowerSigner: Signer,
  poolAddr: EthAddr,
  eventId: string,
  requestedAssets: ERC20Wei,

  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('borrowerReleaseWithdrawsForFlex ', eventId)
  logOT.debug('repay borrowerReleaseWithdrawsForFlex', eventId)

  const uuid = txUUID()
  webSocket?.send(
    wsServerActionPoolTXRequest(
      ActivityEnum.BOPRRWER_TRANSFER_REPAY_FLEX_WITHDRAWAL,
      poolAddr,
      uuid,
      chainConfigurationName
    )
  )
  const providerWritable = borrowerSigner.provider as ProviderWritable

  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, providerWritable)
  const poolType = Number(await poolBaseContract.poolType())
  if (poolType === PoolTypeEnum.FlexibleRate) {
    const poolFlexContract = new Contract(poolAddr, PoolFlexAbi, borrowerSigner)

    const tx2 = await poolFlexContract.repayLoans(requestedAssets, eventId)

    webSocket?.send(
      wsServerActionPoolTX(
        ActivityEnum.BOPRRWER_TRANSFER_REPAY_FLEX_WITHDRAWAL,
        poolAddr,
        uuid,
        tx2.hash as TxHash,
        chainConfigurationName
      )
    )

    tx2.wait(1)

    logOT.debug('borrowerReleaseWithdrawsForFlex success', poolAddr)
  } else {
    throw new Error('Pool is not flexible rate')
  }
}

export const redeemRequestFromPoolFlex = async (
  webSocket: WebSocket | undefined,
  lenderSigner: Signer,
  poolAddr: EthAddr,
  shares: ERC20Wei,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('redeemRequestFromPool', shares, poolAddr)

  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.WITHDRAW_REQUEST, poolAddr, uuid, chainConfigurationName))
  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, lenderSigner)
  const poolType = Number(await poolBaseContract.poolType())
  if (poolType === PoolTypeEnum.FlexibleRate) {
    const poolFlexContract = new Contract(poolAddr, PoolFlexAbi, lenderSigner)
    const accountAddr: EthAddr = await lenderSigner.getAddress()
    const maxRedeemRequest = await poolFlexContract.maxRedeemRequest(accountAddr)
    logOT.debug('maxRedeemRequest', maxRedeemRequest.toString(), accountAddr)
    const maxRedeemNumber = erc20WeiToNumber(maxRedeemRequest.toString() as ERC20Wei)
    let tx2
    if (erc20WeiToNumber(shares) > maxRedeemNumber) {
      tx2 = await poolFlexContract.requestRedeem(maxRedeemRequest)
      logOT.debug('redeemFromPool redeemTx')
    } else {
      logOT.debug('maxRedeem', shares.toString(), accountAddr)
      tx2 = await poolFlexContract.requestRedeem(shares)
      logOT.debug('redeemFromPool redeemTx')
    }

    webSocket?.send(
      wsServerActionPoolTX(ActivityEnum.WITHDRAW_REQUEST, poolAddr, uuid, tx2.hash as TxHash, chainConfigurationName)
    )
    await tx2.wait()
  } else {
    throw new Error('Pool is not flexible rate')
  }
}

export const dailyInterestAccrualForFlex = async (
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolAddr: EthAddr,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('dailyFlexAccrual ')

  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.DAILY_ACCRUAL_FLEX, poolAddr, uuid, chainConfigurationName))

  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, poolAdminSigner)
  const poolType = Number(await poolBaseContract.poolType())

  if (poolType === PoolTypeEnum.FlexibleRate) {
    const poolFlexContract = new Contract(poolAddr, PoolFlexAbi, poolAdminSigner)

    const tx2 = await poolFlexContract.dailyInterestAccural()

    webSocket?.send(
      wsServerActionPoolTX(ActivityEnum.DAILY_ACCRUAL_FLEX, poolAddr, uuid, tx2.hash as TxHash, chainConfigurationName)
    )

    await tx2.wait(1)
    logOT.debug('dailyInterestAccrual success', poolAddr)
  } else {
    throw new Error('Pool is not flexible rate')
  }
}

export const depositAndApproveToPool = async (
  webSocket: WebSocket | undefined,
  lenderSigner: Signer,
  providerReadOnly: ProviderReadOnly,
  poolAddr: EthAddr,
  depositAmountWei: ERC20Wei,
  chainConfigurationName: string
): Promise<void> => {
  await approveDepositToPool(
    webSocket,
    lenderSigner,
    providerReadOnly,
    poolAddr,
    depositAmountWei,
    chainConfigurationName
  )
  await depositToPool(webSocket, lenderSigner, providerReadOnly, poolAddr, depositAmountWei, chainConfigurationName)
}
export const depositToPool = async (
  webSocket: WebSocket | undefined,
  lenderSigner: Signer,
  providerReadOnly: ProviderReadOnly,
  poolAddr: EthAddr,
  depositAmountWei: ERC20Wei,
  chainConfigurationName: string
): Promise<void> => {
  try {
    const uuid = txUUID()
    webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.POOL_DEPOSIT, poolAddr, uuid, chainConfigurationName))
    logOT.debug('depositOnlyToPool', poolAddr, !!lenderSigner, depositAmountWei)
    // Note: Using Pool since the signature for liquidityAssetAddr() is the same across all pool types
    const poolBaseContract = new Contract(poolAddr, PoolAbi, lenderSigner) as unknown as Pool

    const liquidityAsset = (await poolBaseContract.liquidityAssetAddr()) as EthAddr

    logOT.info('depositOnlyToPool liquidityAsset ', liquidityAsset)
    const erc20Contract = new Contract(liquidityAsset, Erc20Abi, providerReadOnly)
    const lenderAddr: EthAddr = await lenderSigner.getAddress()

    logOT.debug('depositOnlyToPool', lenderAddr, poolAddr, liquidityAsset)
    const currentAllowance = await erc20Contract.allowance(lenderAddr, poolAddr)
    const currentBalance = await erc20Contract.balanceOf(lenderAddr)

    logOT.info('depositOnlyToPool currentBalance currentAllowance', liquidityAsset, currentBalance, currentAllowance)
    logOT.info('depositToPool currentAllowance', lenderAddr, poolAddr, currentAllowance.toString())
    logOT.info('depositToPool Sending Deposit', depositAmountWei)

    // Note: Using Pool since the signature for liquidityAssetAddr() is the same across all pool types
    const depositToPoolTx = (await poolBaseContract?.deposit(
      depositAmountWei,
      lenderAddr
    )) as unknown as ethers.ContractTransactionResponse

    webSocket?.send(
      wsServerActionPoolTX(
        ActivityEnum.POOL_DEPOSIT,
        poolAddr,
        uuid,
        depositToPoolTx.hash as TxHash,
        chainConfigurationName
      )
    )

    logOT.debug('depositToPool Deposit Sent')
    const depositToPoolTx2 = await depositToPoolTx.wait(1)
    logOT.debug('depositToPool Deposit Waited')
    logOT.debug('depositToPool depositToPoolTx', depositToPoolTx2)
  } catch (e: unknown) {
    const error = e as ChainError

    if (error.reason === 'repriced') {
      return
    }

    if (error?.error?.data?.originalError) {
      const poolContract = new Contract(poolAddr, PoolAbi, providerReadOnly)
      const poolType = Number(await poolContract.poolType())

      logOT.debug('poolType', poolType)
      if (poolType === PoolTypeEnum.Dynamic) {
        logOT.debug("It's a flexible rate pool")
        const poolDynamicContract = new Contract(poolAddr, PoolDynamicAbi, providerReadOnly)
        const decodedError = poolDynamicContract.interface.parseError(
          error.error?.data?.originalError.data as unknown as BytesLike
        )
        logOT.debug('Decoded error Flex', decodedError)
        throw e
      } else if (poolType === PoolTypeEnum.FlexibleRate) {
        logOT.debug("It's a flexible rate pool")

        const poolFlexContract = new Contract(poolAddr, PoolFlexAbi, providerReadOnly)
        const decodedError = poolFlexContract.interface.parseError(
          error.error?.data?.originalError.data as unknown as BytesLike
        )
        logOT.error('Decoded error 11', decodedError)
        throw e
      } else {
        const decodedError = poolContract.interface.parseError(
          error.error?.data?.originalError.data as unknown as BytesLike
        )
        logOT.error('Decoded error 222', decodedError)
        throw e
      }
    }

    if (Object.keys(e as { error: ChainError }).includes('error')) {
      const error = (e as { error: ChainError }).error as ChainError

      logOT.error('depositToPool error 1', error.reason)

      throw error
    }

    logOT.error('depositToPool error 2', error.reason)

    throw e
  }
}
export const approveDepositToPool = async (
  webSocket: WebSocket | undefined,
  lenderSigner: Signer,
  providerReadOnly: ProviderReadOnly,
  poolAddr: EthAddr,
  depositAmountWei: ERC20Wei,
  chainConfigurationName: string
): Promise<ethers.ContractTransactionResponse | undefined> => {
  let approveReceipt: ethers.ContractTransactionResponse | undefined = undefined
  const uuid = txUUID()
  webSocket?.send(
    wsServerActionPoolTXRequest(ActivityEnum.POOL_DEPOSIT_APPROVE, poolAddr, uuid, chainConfigurationName)
  )

  logOT.debug('approveDepositToPool 1 ', depositAmountWei)
  const lenderAddr: EthAddr = await lenderSigner.getAddress()
  const poolContract = new Contract(poolAddr, PoolAbi, providerReadOnly)

  const liquidityAsset = await poolContract.liquidityAssetAddr()
  const erc20Contract = new Contract(liquidityAsset, Erc20Abi, providerReadOnly)
  const currentAllowance = await erc20Contract.allowance(lenderAddr, poolAddr)
  logOT.debug('approveDepositToPool 3 currentAllowance', currentAllowance.toString())

  const approveAmount = depositAmountWei

  logOT.debug('approveDepositToPool 4 currentAllowance', approveAmount, depositAmountWei, approveAmount.toString())

  const signerRpcAddress = await lenderSigner.provider?.getNetwork()
  logOT.debug('approveDepositToPool signer RPC address', signerRpcAddress)

  const erc20ContractSigned = new Contract(liquidityAsset, Erc20Abi, lenderSigner)
  const _approveReceipt = await erc20ContractSigned.approve(poolAddr, approveAmount)
  approveReceipt = _approveReceipt

  webSocket?.send(
    wsServerActionPoolTX(
      ActivityEnum.POOL_DEPOSIT_APPROVE,
      poolAddr,
      uuid,
      approveReceipt?.hash as TxHash,
      chainConfigurationName
    )
  )
  await _approveReceipt?.wait(1)

  logOT.debug('approveDepositToPool approveReceipt', approveReceipt)
  return approveReceipt
}
